import { Link } from 'react-router-dom';
import { openConsentSettings } from '../../lib/analytics';
import { useT } from '@/i18n';
import { useSession } from '@/hooks/session';
import { COMPANY, GRIEVANCE_OFFICER } from '@/content/company';
import { CrisisNote } from '@/components/trust/CrisisNote';
import { Shield } from '@/components/ui';
import { cx } from '@/components/ui/cx';

const LINK = 'inline-flex min-h-[36px] items-center text-lagoon-100 hover:text-white hover:underline underline-offset-4';

type FooterLink = { key: string; to: string; external?: boolean };

/** Columns of legal, trust and help links — deliberately not a copy of the header nav. */
export const FOOTER_COLUMNS: Array<{ title: string; links: FooterLink[] }> = [
  {
    title: 'footer.col.nurture',
    links: [
      { key: 'nav.homePlan', to: '/home-plan' },
      { key: 'nav.howItWorks', to: '/how-it-works' },
      { key: 'landing.faq', to: '/faq' },
    ],
  },
  {
    title: 'footer.col.learn',
    links: [
      { key: 'learn.group.conditions', to: '/learn#conditions' },
      { key: 'learn.group.situations', to: '/learn#situations' },
      { key: 'learn.group.guides', to: '/learn#guides' },
    ],
  },
  {
    title: 'footer.col.trust',
    links: [
      { key: 'footer.trust', to: '/trust' },
      { key: 'nav.pricing', to: '/pricing' },
      { key: 'footer.privacy', to: '/privacy' },
      { key: 'footer.terms', to: '/terms' },
      { key: 'footer.refunds', to: '/refunds' },
      { key: 'footer.dataRequest', to: '/data-request' },
    ],
  },
];

export const Footer = () => {
  const t = useT();
  const { account } = useSession();
  return (
    <footer className={cx('bg-ink text-lagoon-100', account && 'pb-20 nav:pb-0')}>
      <div className="mx-auto max-w-content px-4 pb-8 pt-12 md:px-6">
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 nav:grid-cols-[1.3fr_1fr_1fr_1fr_1.2fr]">
          <div className="col-span-2 max-w-[22rem] nav:col-span-1">
            <p className="flex items-center gap-2">
              <img src="/brand/logo-mark.png" alt="" width={36} height={36} className="h-9 w-9 rounded-full bg-white p-0.5" />
              <span className="font-display text-[26px] font-extrabold leading-none text-white">{t('app.name')}</span>
              <span className="text-caption text-lagoon-200">{t('app.by')}</span>
            </p>
            <p className="mt-3 text-label font-semibold text-lagoon-100">{t('footer.tagline')}</p>
            <Link
              to="/privacy#grievance-officer"
              className="mt-5 inline-flex min-h-tap items-center gap-2 rounded-pill border border-lagoon-200/40 px-4 text-label text-white hover:border-white hover:bg-white/5"
            >
              <Shield className="h-4 w-4" />
              {t('footer.grievance')}
            </Link>
          </div>

          {FOOTER_COLUMNS.map((col) => (
            <nav key={col.title} aria-label={t(col.title)}>
              <p className="eyebrow text-lagoon-200">{t(col.title)}</p>
              <ul className="mt-3 grid gap-0.5 text-label">
                {col.links.map((l) => (
                  <li key={l.to}>
                    {l.external ? (
                      <a href={l.to} className={LINK}>
                        {t(l.key)}
                      </a>
                    ) : (
                      <Link to={l.to} className={LINK}>
                        {t(l.key)}
                      </Link>
                    )}
                  </li>
                ))}
                {col.title === 'footer.col.trust' && (
                  <li>
                    <button type="button" onClick={openConsentSettings} className={LINK}>
                      {t('consent.link')}
                    </button>
                  </li>
                )}
              </ul>
            </nav>
          ))}

          <div className="col-span-2 nav:col-span-1">
            <p className="eyebrow text-lagoon-200">{t('footer.col.contact')}</p>
            <ul className="mt-3 grid gap-0.5 text-label">
              <li>
                <a className={LINK} href={`mailto:${COMPANY.supportEmail}`}>
                  {COMPANY.supportEmail}
                </a>
              </li>
              <li>
                <a className={LINK} href={`mailto:${GRIEVANCE_OFFICER.email}`}>
                  {GRIEVANCE_OFFICER.email}
                </a>
              </li>
              <li>
                <a className={LINK} href={GRIEVANCE_OFFICER.phoneHref}>
                  {GRIEVANCE_OFFICER.phone}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 border-t border-white/10 pt-6 text-lagoon-100/90">
          <CrisisNote tone="dark" className="max-w-[96ch]" />
        </div>

        <div className="mt-6 flex flex-col gap-2 border-t border-white/10 pt-6 text-caption text-lagoon-200 nav:flex-row nav:items-center nav:justify-between">
          <p>
            © 2026 {COMPANY.legalName} · CIN {COMPANY.cin}
          </p>
          <p>
            {t('footer.family')}{' '}
            <a href={COMPANY.website} target="_blank" rel="noopener noreferrer" className="font-bold text-white hover:underline">
              zenquip.com
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};
